import { Link } from 'react-router';
import { capitalize } from 'lodash';

import { useSoftwareEscrow } from 'src/context/software-escrow';
import { SoftwareOnboardingMain } from 'src/components/SoftwareOnboarding';
import { AgreementFormEnum, EscrowPartyTypeEnum } from 'src/graphql/schema';
import { PartyInvitationContainer } from 'src/containers/Escrow/Invitation';
import { EscrowBeneficiaryCore } from 'src/containers/Escrow/Beneficiary';

const agreementFormContentMap = {
  [AgreementFormEnum.Bipartite]: {
    title: 'Beneficiary information and representatives',
    description: 'Enter the beneficiary organization details and add their key points of contact for this escrow.',
    noteText: undefined,
  },
  [AgreementFormEnum.Tripartite]: {
    creator: {
      title: 'Beneficiary information and representatives',
      description:
        'Check your organization details and add the key points of contact and authorized signatories for this escrow.',
      noteText: (
        <>
          Once all parties approve the escrow, we’ll send e-signature requests via DropBox Sign to the designated
          signatories to make the agreement legally binding.
          <br />
          Need a different signing method or a hardcopy agreement?{' '}
          <Link to="/help-resources/contact-us" className="underline!">
            Contact us
          </Link>
          .
        </>
      ),
    },
    invitation: {
      title: 'Beneficiary information',
      description:
        'Add the beneficiary organization and its primary point of contact. The beneficiary is the entity authorized to request the release of escrowed material in the event of a release trigger.',
      noteText:
        'The primary contact will be invited to the platform, and be able to add members, representatives and signatories for their organization.',
    },
  },
};

const getBeneficiaryContent = (agreementForm: AgreementFormEnum, isEscrowInvitation: boolean) => {
  if (agreementForm === AgreementFormEnum.Tripartite)
    return agreementFormContentMap.tripartite[isEscrowInvitation ? 'invitation' : 'creator'];

  return agreementFormContentMap.bipartite;
};

const BeneficiaryContent = () => {
  const {
    softwareEscrow: { id: escrowId, currentCompanyRole, agreement, beneficiary, escrowInvitation, policy },
  } = useSoftwareEscrow();

  if (!beneficiary && agreement?.agreementForm === AgreementFormEnum.Tripartite) {
    const invitePartyType =
      currentCompanyRole === EscrowPartyTypeEnum.Beneficiary
        ? EscrowPartyTypeEnum.Depositor
        : EscrowPartyTypeEnum.Beneficiary;

    return (
      <div className="col-span-2 flex justify-center">
        <div className="w-[580px] max-w-full">
          <PartyInvitationContainer
            escrowId={escrowId}
            invitationId={escrowInvitation?.id ?? null}
            invitePartyType={invitePartyType}
            canInviteParty={policy[`invite${capitalize(invitePartyType)}`]}
          />
        </div>
      </div>
    );
  }

  return (
    <EscrowBeneficiaryCore
      beneficiaryId={beneficiary?.id ?? ''}
      escrowId={escrowId}
      agreementType={agreement!.agreementForm!}
      canCreateBeneficiary={policy.createBeneficiary}
    />
  );
};

const SoftwareEscrowOnboardingBeneficiary = () => {
  const {
    softwareEscrow: { agreement, currentCompanyRole },
  } = useSoftwareEscrow();

  const isEscrowInvitation =
    currentCompanyRole !== EscrowPartyTypeEnum.Beneficiary && agreement!.agreementForm === AgreementFormEnum.Tripartite;
  const { title, description, noteText } = getBeneficiaryContent(agreement!.agreementForm!, isEscrowInvitation);

  return (
    <SoftwareOnboardingMain
      title={title}
      description={description}
      noteText={noteText}
      classes={{ wrapper: !isEscrowInvitation ? 'md:h-full min-h-fit' : '' }}
    >
      <div className="container mx-auto grow md:overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:h-full">
          <BeneficiaryContent />
        </div>
      </div>
    </SoftwareOnboardingMain>
  );
};

export default SoftwareEscrowOnboardingBeneficiary;
